/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Cookies from 'js-cookie';
import ReactLoading from 'react-loading';
import { toast } from 'react-toastify';
import { MdChevronLeft } from 'react-icons/md';
import { IconButton } from '@material-ui/core';
import { formatCurrency } from 'react-data-formatter';

import api from 'api';
import { Header, ConfirmDialog } from 'components';
import BackofficeMenu from 'components/BackofficeMenu';

import {
  Container,
  ContentArea,
  LoadingArea,
  Title,
  DiscountAlert,
  SubmitButton,
  CancelButton,
  Label,
  Section,
  SectionTitle,
} from './styles';

const Endorsement = ({ match }) => {
  const history = useHistory();
  const policyId = match.params.id;

  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);

  const [policyData, setPolicyData] = useState(null);
  const [quotationData, setQuotationData] = useState(null);
  const [quotationResponse, setQuotationResponse] = useState(null);
  const [initialValue, setInitialValue] = useState(0);

  const [hasDiscount, setHasDiscount] = useState(false);

  const [availableCoverages, setAvailableCoverages] = useState({
    basic: [],
    additional: [],
    lmi: [],
    deductible: [],
    optional: [],
    glass: [],
    replacement: [],
  });

  const [selectedProducts, setSelectedProducts] = useState({
    basic: null,
    support: null,
    glass: null,
    replacement: null,
    lmi: null,
    deductible: null,
    optionals: [],
  });

  const [vehicleUses, setVehicleUses] = useState([]);
  const [selectedVehicleUse, setSelectedVehicleUse] = useState('');
  const [loadingVehicleUses, setLoadingVehicleUses] = useState(false);
  const [vehicleUsesError, setVehicleUsesError] = useState(false);
  const [initialProducts, setInitialProducts] = useState(null);

  const originalVehicleUse = policyData?.vehicle?.id_vehicle_use != null ? String(policyData.vehicle.id_vehicle_use) : '';
  const vehicleUseChanged = !!selectedVehicleUse && selectedVehicleUse !== originalVehicleUse;

  const loadVehicleUses = async (vehicle) => {
    setLoadingVehicleUses(true);
    setVehicleUsesError(false);
    try {
      const resp = await api.get('/list-vehicle-uses', {
        headers: {
          Authorization: Cookies.get('splitrisk-authtoken'),
        },
        params: {
          type: vehicle?.id_vehicle_type,
        },
      });

      const uses = resp?.data || [];
      const currentUse = { id: vehicle?.id_vehicle_use, name: vehicle?.vehicle_use };

      const usesWithCurrent =
        currentUse.id != null && currentUse.name && !uses.some((item) => Number(item.id) === Number(currentUse.id))
          ? [...uses, currentUse]
          : uses;

      setVehicleUses(usesWithCurrent);

      setSelectedVehicleUse(vehicle?.id_vehicle_use != null ? String(vehicle.id_vehicle_use) : '');
    } catch (error) {
      console.error('Erro ao carregar os usos do veículo:', error.response?.data || error);
      setVehicleUsesError(true);
    } finally {
      setLoadingVehicleUses(false);
    }
  };

  const loadAvailableCoverages = async (policy) => {
    try {
      const vehicle = policy?.vehicle;
      const isMotorcycle = [4, 7].includes(vehicle?.id_vehicle_type);
      const motorType = isMotorcycle ? 2 : 1;

      const commonData = {
        fipe_code: vehicle?.fipe_code,
        brand: vehicle?.id_brand,
        value: vehicle?.value,
        type_vehicle: motorType,
        zero_km: vehicle?.zero_km,
        cep: policy?.TB_ADDRESS?.cep || policy?.TB_ADDRESS?.zip_code,
        exclusive: false,
      };

      const [basic, support, replacement, glass, optional, lmi, deductible] = await Promise.all([
        api.post(
          '/quotation/get-basic-coverage',
          { fipe_code: vehicle?.fipe_code, brand: vehicle?.id_brand, value: vehicle?.value },
          { headers: { Authorization: Cookies.get('splitrisk-authtoken') } },
        ),
        api.post('/quotation/get-support-coverage', commonData, {
          headers: { Authorization: Cookies.get('splitrisk-authtoken') },
        }),
        api.post('/quotation/get-replacement-car-coverage', commonData, {
          headers: { Authorization: Cookies.get('splitrisk-authtoken') },
        }),
        api.post('/quotation/get-glass-coverage', commonData, { headers: { Authorization: Cookies.get('splitrisk-authtoken') } }),
        api.post('/quotation/get-optional-coverage', commonData, {
          headers: { Authorization: Cookies.get('splitrisk-authtoken') },
        }),
        api.post(
          '/quotation/get-fipe-lmi-coverage',
          { ...commonData, from_auction: false },
          { headers: { Authorization: Cookies.get('splitrisk-authtoken') } },
        ),
        api.post('/quotation/get-deductible-coverage', commonData, {
          headers: { Authorization: Cookies.get('splitrisk-authtoken') },
        }),
      ]);

      setAvailableCoverages({
        basic: basic.data || [],
        additional: support.data || [],
        optional: optional.data || [],
        glass: glass.data || [],
        replacement: replacement.data || [],
        lmi: lmi.data || [],
        deductible: deductible.data || [],
      });
    } catch (error) {
      console.error('Erro ao carregar coberturas disponiveis:', error);
      toast.error('Erro ao listar coberturas do motor');
    }
  };

  const handleQuotation = async (baseData, products) => {
    if (!products || Object.values(products).every((val) => val === null || val?.length === 0)) return;

    setCalculating(true);
    try {
      const response = await api({
        method: 'POST',
        url: '/endorsement/quotation-preview',
        headers: {
          'Content-Type': 'application/json',
          Authorization: Cookies.get('splitrisk-authtoken'),
        },
        data: {
          ...baseData,
          products,
        },
      });

      setQuotationResponse(response.data);
    } catch (error) {
      console.error(error);
      toast.error('Erro ao realizar o recalculo');
    } finally {
      setCalculating(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const resp = await api({
        method: 'GET',
        url: `/splitrisk-policy/${policyId}`,
        headers: { Authorization: Cookies.get('splitrisk-authtoken') },
      });

      const policy = resp.data;
      setPolicyData(policy);
      setSelectedVehicleUse(policy?.vehicle?.id_vehicle_use != null ? String(policy.vehicle.id_vehicle_use) : '');
      setInitialValue(policy?.value || 0);
      await loadVehicleUses(policy?.vehicle);

      const logs = policy?.TB_PROPOSAL?.TB_LOG_PROPOSAL || [];

      const checkDiscount = logs.some((log) => log.description && log.description.includes('discount_percentage'));
      setHasDiscount(checkDiscount);

      const initialQuotation = {
        vehicle: {
          id_brand: policy?.vehicle?.id_brand,
          fipe_code: policy?.vehicle?.fipe_code,
          value: policy?.vehicle?.value,
          id_type: policy?.vehicle?.id_vehicle_type,
          zero_km: policy?.vehicle?.zero_km,
          id_vehicle_use: policy?.vehicle?.id_vehicle_use,
          manufacture_year: policy?.vehicle?.manufacture_year,
          year_model: policy?.vehicle?.year_model,
          gnv: policy?.vehicle?.gnv,
          eletric_hybrid: policy?.vehicle?.eletric_hybrid,
          some_driver_18_25_years_old: policy?.vehicle?.some_driver_18_25_years_old,
        },
        quotationProposalData: { quotationProposalId: policy?.TB_PROPOSAL?.id },
        address: policy?.TB_ADDRESS,
        policy_modality: policy?.modality,
        comission: policy?.TB_PROPOSAL?.comission || 0.1,
      };

      setQuotationData(initialQuotation);

      const qp = policy?.TB_PROPOSAL?.TB_QUOTATION_PROPOSAL?.[0];
      const initialSelection = {
        basic: qp?.TB_PRICING_BASIC_COVERAGE_TB_QUOTATION_PROPOSAL?.[0]?.id_pricing_basic_coverage || null,
        support: qp?.TB_PRICING_SUPPORT_TB_QUOTATION_PROPOSAL?.[0]?.id_pricing_support || null,
        glass: qp?.TB_PRICING_GLASS_TB_QUOTATION_PROPOSAL?.[0]?.id_pricing_glass || null,
        replacement: qp?.TB_PRICING_REPLACEMENT_CAR_TB_QUOTATION_PROPOSAL?.[0]?.id_pricing_replacement_car || null,
        lmi: qp?.TB_PRICING_FIPE_LMI_TB_QUOTATION_PROPOSAL?.[0]?.id_pricing_fipe_lmi || null,
        deductible: qp?.TB_PRICING_DEDUCTIBLE_TB_QUOTATION_PROPOSAL?.[0]?.id_pricing_deductible || null,
        optionals: qp?.TB_PRICING_OPTIONAL_COVERAGE_TB_QUOTATION_PROPOSAL?.map((i) => i.id_pricing_optional_coverage) || [],
      };

      setSelectedProducts(initialSelection);
      setInitialProducts(initialSelection);
      await loadAvailableCoverages(policy);
      await handleQuotation(initialQuotation, initialSelection);

      setLoading(false);
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar dados do contrato');
      setLoading(false);
    }
  };

  const handleSelectChange = (category, value) => {
    let newSelection = { ...selectedProducts };

    if (category === 'optionals') {
      const parsedValue = parseInt(value);
      if (value === '') {
        newSelection.optionals = [];
      } else {
        const isSelected = selectedProducts.optionals.includes(parsedValue);
        newSelection.optionals = isSelected
          ? selectedProducts.optionals.filter((id) => id !== parsedValue)
          : [...selectedProducts.optionals, parsedValue];
      }
    } else {
      newSelection[category] = value ? parseInt(value) : null;
    }

    setSelectedProducts(newSelection);
    handleQuotation(quotationData, newSelection);
  };

  const handleVehicleUseChange = (value) => {
    setSelectedVehicleUse(value);

    const updatedQuotationData = {
      ...quotationData,
      vehicle: {
        ...quotationData?.vehicle,
        id_vehicle_use: value ? Number(value) : policyData?.vehicle?.id_vehicle_use,
      },
    };

    setQuotationData(updatedQuotationData);
    handleQuotation(updatedQuotationData, selectedProducts);
  };

  const handleConfirmSave = async () => {
    try {
      setCalculating(true);
      await api({
        method: 'POST',
        url: `/endorsement/process/${policyData?.TB_PROPOSAL?.id}`,
        headers: {
          'Content-Type': 'application/json',
          Authorization: Cookies.get('splitrisk-authtoken'),
        },
        data: {
          products: selectedProducts,
          value: quotationResponse?.value,
          ...(vehicleUseChanged && { id_vehicle_use: Number(selectedVehicleUse) }),
        },
      });

      const productsChanged = JSON.stringify(selectedProducts) !== JSON.stringify(initialProducts);
      toast.success(
        vehicleUseChanged && !productsChanged
          ? 'Uso do veículo alterado com sucesso'
          : 'Alteração de coberturas realizada com sucesso!',
      );
      history.push(`/backoffice/detalhes-do-contrato/${policyId}`);
    } catch (error) {
      console.error(error);
      toast.error('Erro ao salvar as alterações');
    } finally {
      setCalculating(false);
      setOpenConfirm(false);
    }
  };

  const renderSelectedCoverages = () => {
    const detailsList = [];

    const getInitialValueFromPolicy = (category, id) => {
      const qp = policyData?.TB_PROPOSAL?.TB_QUOTATION_PROPOSAL?.[0];
      if (!qp) return null;

      const tableMap = {
        basic: qp.TB_PRICING_BASIC_COVERAGE_TB_QUOTATION_PROPOSAL,
        support: qp.TB_PRICING_SUPPORT_TB_QUOTATION_PROPOSAL,
        glass: qp.TB_PRICING_GLASS_TB_QUOTATION_PROPOSAL,
        replacement: qp.TB_PRICING_REPLACEMENT_CAR_TB_QUOTATION_PROPOSAL,
        lmi: qp.TB_PRICING_FIPE_LMI_TB_QUOTATION_PROPOSAL,
        deductible: qp.TB_PRICING_DEDUCTIBLE_TB_QUOTATION_PROPOSAL,
        optional: qp.TB_PRICING_OPTIONAL_COVERAGE_TB_QUOTATION_PROPOSAL,
      };

      const keyMap = {
        basic: 'id_pricing_basic_coverage',
        support: 'id_pricing_support',
        glass: 'id_pricing_glass',
        replacement: 'id_pricing_replacement_car',
        lmi: 'id_pricing_fipe_lmi',
        deductible: 'id_pricing_deductible',
        optional: 'id_pricing_optional_coverage',
      };

      const relTable = tableMap[category];
      const targetKey = keyMap[category];

      if (!relTable || !Array.isArray(relTable) || !targetKey) return null;

      const relItem = relTable.find((item) => item[targetKey] === id);
      return relItem?.final_value ?? relItem?.value ?? null;
    };

    const getQuotedItem = (category, id) => {
      const details = quotationResponse?.details;
      if (!details) return null;

      switch (category) {
        case 'basic':
          return details.basicCoverage?.id === id ? details.basicCoverage : null;
        case 'support':
          return details.suportCoverage?.id === id ? details.suportCoverage : null;
        case 'glass':
          return details.glassCoverage?.id === id ? details.glassCoverage : null;
        case 'replacement':
          return details.replacementCarCoverage?.id === id ? details.replacementCarCoverage : null;
        case 'optional':
          return details.optionalCoverages?.find((opt) => opt.id === id);
        default:
          return null;
      }
    };

    const addDetail = (list, id, category) => {
      if (!id) return;
      const item = list.find((i) => i.id === id);

      if (item) {
        const quotedItem = getQuotedItem(category, id);

        const policyItemValue = getInitialValueFromPolicy(category, id);

        const rawVal = quotedItem?.finalPrice ?? quotedItem?.value ?? policyItemValue ?? item?.final_value ?? item?.value ?? 0;

        detailsList.push({ name: item.name, valueStr: formatCurrency(rawVal) });
      }
    };

    addDetail(availableCoverages.basic, selectedProducts.basic, 'basic');
    addDetail(availableCoverages.additional, selectedProducts.support, 'support');
    addDetail(availableCoverages.glass, selectedProducts.glass, 'glass');
    addDetail(availableCoverages.replacement, selectedProducts.replacement, 'replacement');

    selectedProducts.optionals.forEach((optId) => {
      addDetail(availableCoverages.optional, optId, 'optional');
    });

    return detailsList;
  };

  useEffect(() => {
    loadData();
  }, [policyId]);

  if (loading) {
    return (
      <>
        <Header />
        <Container>
          <LoadingArea>
            <ReactLoading color="rgb(0 0 204)" height={40} width={40} type="spin" />
          </LoadingArea>
        </Container>
      </>
    );
  }

  const selectedCoveragesList = renderSelectedCoverages();

  const selectedLmiObj = availableCoverages.lmi.find((item) => item.id === selectedProducts.lmi);
  const originalFipe = policyData?.vehicle?.value || 0;
  const lmiPercentage = selectedLmiObj ? selectedLmiObj.lmi : 100;
  const fipeLmiValue = (originalFipe * lmiPercentage) / 100;

  const renderOptionalSelect = (label, type) => {
    const options = availableCoverages.optional.filter((item) => item.type === type);
    if (!options.length) return null;
    const isOfType = (id) => options.some((opt) => opt.id === id);

    return (
      <div style={{ marginTop: '15px' }}>
        <Label>{label}:</Label>
        <Section
          value={selectedProducts.optionals.find(isOfType) || ''}
          onChange={(e) => {
            const newId = e.target.value ? parseInt(e.target.value) : null;
            const newOptionals = selectedProducts.optionals.filter((id) => !isOfType(id));
            if (newId) newOptionals.push(newId);

            const newSelection = { ...selectedProducts, optionals: newOptionals };
            setSelectedProducts(newSelection);
            handleQuotation(quotationData, newSelection);
          }}
        >
          <option value="">Nenhum</option>
          {options.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </Section>
      </div>
    );
  };

  return (
    <>
      <Header />
      <Container>
        <ContentArea>
          <BackofficeMenu policyId={policyId} policyData={policyData} />

          <Title style={{ display: 'flex', alignItems: 'center', marginTop: 20, marginBottom: 30 }}>
            <IconButton
              size="small"
              style={{ marginRight: 10 }}
              onClick={() => history.push(`/backoffice/detalhes-do-contrato/${policyId}`)}
            >
              <MdChevronLeft size={24} />
            </IconButton>
            Endosso de Coberturas
          </Title>

          <div style={{ display: 'flex', gap: '40px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ flex: '2', minWidth: '300px' }}>
              <SectionTitle>
                Coberturas básicas <span style={{ color: 'red' }}>*</span>
              </SectionTitle>
              <Section value={selectedProducts.basic || ''} onChange={(e) => handleSelectChange('basic', e.target.value)}>
                <option value="">Selecione uma cobertura básica</option>
                {availableCoverages.basic.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </Section>

              <SectionTitle>Coberturas adicionais</SectionTitle>

              <div style={{ display: 'flex', gap: '20px', marginBottom: '15px' }}>
                <div style={{ flex: 1 }}>
                  <Label>Assistência 24h:</Label>
                  <Section value={selectedProducts.support || ''} onChange={(e) => handleSelectChange('support', e.target.value)}>
                    <option value="">Selecione</option>
                    {availableCoverages.additional.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </Section>
                </div>

                <div style={{ flex: 1 }}>
                  <Label>Limite máximo de indenização (LMI):</Label>
                  <Section value={selectedProducts.lmi || ''} onChange={(e) => handleSelectChange('lmi', e.target.value)}>
                    <option value="">100%</option>
                    {availableCoverages.lmi.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </Section>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '20px' }}>
                <div style={{ flex: 1 }}>
                  <Label>Carro Reserva:</Label>
                  <Section
                    value={selectedProducts.replacement || ''}
                    onChange={(e) => handleSelectChange('replacement', e.target.value)}
                  >
                    <option value="">Selecione</option>
                    {availableCoverages.replacement.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </Section>
                </div>

                <div style={{ flex: 1 }}>
                  <Label>Quebra de Vidros:</Label>
                  <Section value={selectedProducts.glass || ''} onChange={(e) => handleSelectChange('glass', e.target.value)}>
                    <option value="">Selecione</option>
                    {availableCoverages.glass.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </Section>
                </div>
              </div>

              <SectionTitle>Coberturas Opcionais</SectionTitle>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div>
                  <div>
                    <Label>Franquia:</Label>
                    <Section
                      value={selectedProducts.deductible || ''}
                      onChange={(e) => handleSelectChange('deductible', e.target.value)}
                    >
                      <option value="">Selecione</option>
                      {availableCoverages.deductible.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                    </Section>
                  </div>

                  {availableCoverages.optional.filter((item) => item.name.toLowerCase().includes('app')).length > 0 && (
                    <div style={{ marginTop: '15px' }}>
                      <Label>APP:</Label>
                      <Section
                        value={
                          selectedProducts.optionals.find((id) =>
                            availableCoverages.optional.some((opt) => opt.id === id && opt.name.toLowerCase().includes('app')),
                          ) || ''
                        }
                        onChange={(e) => {
                          const newId = e.target.value ? parseInt(e.target.value) : null;
                          let newOptionals = selectedProducts.optionals.filter(
                            (id) =>
                              !availableCoverages.optional.some((opt) => opt.id === id && opt.name.toLowerCase().includes('app')),
                          );
                          if (newId) newOptionals.push(newId);

                          const newSelection = { ...selectedProducts, optionals: newOptionals };
                          setSelectedProducts(newSelection);
                          handleQuotation(quotationData, newSelection);
                        }}
                      >
                        <option value="">Nenhum</option>
                        {availableCoverages.optional
                          .filter((item) => item.name.toLowerCase().includes('app'))
                          .map((item) => (
                            <option key={item.id} value={item.id}>
                              {item.name}
                            </option>
                          ))}
                      </Section>
                    </div>
                  )}
                </div>

                <div>
                  {availableCoverages.optional.filter((item) => item.name.toLowerCase().includes('terceiro')).length > 0 && (
                    <div>
                      <Label>Danos a Terceiros:</Label>
                      <Section
                        value={
                          selectedProducts.optionals.find((id) =>
                            availableCoverages.optional.some(
                              (opt) => opt.id === id && opt.name.toLowerCase().includes('terceiro'),
                            ),
                          ) || ''
                        }
                        onChange={(e) => {
                          const newId = e.target.value ? parseInt(e.target.value) : null;
                          let newOptionals = selectedProducts.optionals.filter(
                            (id) =>
                              !availableCoverages.optional.some(
                                (opt) => opt.id === id && opt.name.toLowerCase().includes('terceiro'),
                              ),
                          );
                          if (newId) newOptionals.push(newId);

                          const newSelection = { ...selectedProducts, optionals: newOptionals };
                          setSelectedProducts(newSelection);
                          handleQuotation(quotationData, newSelection);
                        }}
                      >
                        <option value="">Nenhum</option>
                        {availableCoverages.optional
                          .filter((item) => item.name.toLowerCase().includes('terceiro'))
                          .map((item) => (
                            <option key={item.id} value={item.id}>
                              {item.name}
                            </option>
                          ))}
                      </Section>
                    </div>
                  )}
                  {renderOptionalSelect('RCFV Danos Corporais', 'damages_corporais')}
                  {renderOptionalSelect('RCFV Danos Morais', 'damages_morais')}
                </div>
              </div>

              <SectionTitle>Dados do veículo</SectionTitle>

              <div>
                <Label>Uso do veículo:</Label>

                <Section value={selectedVehicleUse} onChange={(e) => handleVehicleUseChange(e.target.value)}>
                  {loadingVehicleUses ? (
                    <option key="loading" value="">
                      Carregando usos...
                    </option>
                  ) : vehicleUsesError ? (
                    <option key="error" value="">
                      Erro ao carregar usos
                    </option>
                  ) : vehicleUses.length > 0 ? (
                    vehicleUses.map((item) => (
                      <option key={item.id} value={String(item.id)}>
                        {item.name}
                      </option>
                    ))
                  ) : (
                    <option key="empty" value="">
                      Nenhum uso disponível
                    </option>
                  )}
                </Section>
              </div>

              {hasDiscount && (
                <DiscountAlert>
                  <span>⚠️</span>
                  <div>
                    <strong>Atenção: Contrato com Desconto Ativo</strong>
                    Este contrato possui um desconto vinculado às coberturas atuais. Ao aplicar novas condições de endosso, o
                    desconto anterior <b>não</b> será mantido no novo cálculo.
                  </div>
                </DiscountAlert>
              )}
            </div>

            <div
              style={{
                flex: '1',
                minWidth: '350px',
                backgroundColor: '#fdfdfd',
                border: '1px solid #eee',
                borderRadius: '12px',
                padding: '24px',
                position: 'sticky',
                top: '20px',
              }}
            >
              <h2 style={{ color: '#00b16a', fontSize: '24px', margin: '0 0 5px 0' }}>Valor total</h2>
              <h3 style={{ color: '#688ca8', fontSize: '24px', margin: '0 0 20px 0', fontWeight: '500' }}>
                {calculating ? 'Calculando...' : formatCurrency(quotationResponse?.value || initialValue)}
              </h3>

              <h4 style={{ color: '#00b16a', fontSize: '18px', margin: '20px 0 10px 0' }}>Dados do veículo</h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: '13px', color: '#688ca8', lineHeight: '1.8' }}>
                <li>
                  Modelo: {policyData?.vehicle?.brand} {policyData?.vehicle?.model}
                </li>
                <li>Ano modelo: {policyData?.vehicle?.year_model}</li>
                <li>Placa: {policyData?.vehicle?.plate}</li>

                <li>Valor FIPE: {formatCurrency(originalFipe)}</li>

                {selectedLmiObj && (
                  <li>
                    LMI ({selectedLmiObj.lmi}%): {formatCurrency(fipeLmiValue)}
                  </li>
                )}

                <li>GNV: {policyData?.vehicle?.gnv ? 'Sim' : 'Não'}</li>
                <li>Condutor entre 18 e 25 anos: {policyData?.vehicle?.some_driver_18_25_years_old ? 'Sim' : 'Não'}</li>
              </ul>

              <h4 style={{ color: '#00b16a', fontSize: '18px', margin: '20px 0 10px 0', fontWeight: 'bold' }}>Coberturas</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginBottom: '30px' }}>
                {selectedCoveragesList.length === 0 && (
                  <span style={{ fontSize: '13px', color: '#999' }}>Nenhuma cobertura selecionada</span>
                )}
                {selectedCoveragesList.map((cov, index) => (
                  <div key={index} style={{ fontSize: '13px', color: '#688ca8', lineHeight: '1.4' }}>
                    <div>{cov.name}</div>
                    <div>{cov.valueStr}</div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <SubmitButton
                  onClick={() => {
                    if (!selectedProducts.basic) {
                      toast.error('É obrigatório selecionar uma cobertura básica para continuar.', {
                        position: toast.POSITION.TOP_RIGHT,
                        theme: 'colored',
                      });
                      return;
                    }
                    setOpenConfirm(true);
                  }}
                  loading={calculating}
                >
                  Salvar Alterações
                </SubmitButton>

                <CancelButton onClick={() => history.push(`/backoffice/detalhes-do-contrato/${policyId}`)}>Cancelar</CancelButton>
              </div>

              <p style={{ fontSize: '12px', color: '#666', marginTop: '20px', textAlign: 'center' }}>
                {!selectedProducts.basic
                  ? 'Por favor, selecione pelo menos uma cobertura básica para continuar.'
                  : 'Verifique os valores antes de efetivar o endosso.'}
              </p>
            </div>
          </div>
        </ContentArea>
      </Container>

      <ConfirmDialog
        open={openConfirm}
        title="Confirmar Alteração de Coberturas"
        content={`O valor do contrato será alterado para ${formatCurrency(
          quotationResponse?.value || 0,
        )}. Deseja confirmar esta operação?`}
        onConfirm={handleConfirmSave}
        onCancel={() => setOpenConfirm(false)}
        loading={calculating}
        centered={true}
      />
    </>
  );
};

export default Endorsement;
